import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropzoneArea as BaseDropzoneArea } from "react-mui-dropzone";
import { CustomButton, CustomTable } from "../../../global/components";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import { LightTooltip, regularFont, theme } from "../../../utils/styles";
import ragChatbotStyles from "../RagChatbot.styles";
import { getDocuments, ragUploadFile } from "../RagChatbotService";

const DropzoneArea = forwardRef<any, any>((props, ref) => (
  <BaseDropzoneArea {...props} ref={ref} />
));

const useStyles = makeStyles({
  dropzone: {
    border: "2px dashed #9053F6",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    height: "calc(100vh - 291px)",
    [theme.breakpoints.down("md")]: {
      height: "40vh",
      padding: "8px",
    },
  },
  icon: {
    display: "none", // Hide DropzoneArea's default icon
  },
  text: {
    ...regularFont,
    color: "#ffffff90",
    fontSize: "22px",
    [theme.breakpoints.down("md")]: {
      fontSize: "16px",
    },
  },
});

interface Document {
  id: string;
  name: string;
  path: string;
  status: string;
}

interface CustomProps {
  setIsLoading: Function;
  uploadingFiles: File[];
  setUploadingFiles: (files: File[]) => void;
  showUploadingTable: boolean;
  setShowUploadingTable: (show: boolean) => void;
  onChatClick: (fileName: string, category: string) => void;
}

const FileUploadArea = (props: CustomProps) => {
  const classes = ragChatbotStyles;
  const dropzoneClasses = useStyles();
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [dropzoneKey, setDropzoneKey] = useState<number>(0);
  const [fileTypes, setFileTypes] = useState<{ [key: string]: string }>({});
  const intervalRef = useRef<any>(null);
  const dropzoneRef = useRef<any>(null);

  const fileTypeOptions = ["Other", "Marriage", "Death", "Birth"];

  const documentsHeader = [
    {
      name: `${t("name")}`,
      field: "name",
    },
    {
      name: `${t("category")}`,
      field: "category",
    },
    {
      name: `${t("status")}`,
      field: "status",
    },
    {
      name: `${t("action")}`,
      field: "action",
    },
  ];

  const getDocumentsData = async (
    page: number,
    rowsPerPage: number,
    isPolling = false
  ) => {
    try {
      !isPolling && props.setIsLoading(true);
      const response = await getDocuments(page, rowsPerPage);
      convertDataForTable(response.documents);
      setCount(response.total);
      props.setIsLoading(false);
      return response.documents;
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
      return [];
    }
  };

  const convertDataForTable = (invoice: any) => {
    const tableValue = invoice.map((item: any) => {
      return {
        id: item.id,
        name: (
          <LightTooltip
            placement="top"
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#ffffff",
                fontSize: "16px",
                color: "#000000",
              },
            }}
            title={item.name}
          >
            <Typography
              component="a"
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              sx={classes.tableLinkText}
            >
              {item.name}
            </Typography>
          </LightTooltip>
        ),
        category: item.category,
        status: item.embedding_status,
        rawStatus: item.embedding_status,
        action: (
          <Box sx={{ display: "flex", gap: 1 }}>
            {item.embedding_status === "Completed" && (
              <ChatIcon
                sx={{
                  color: "#9053F6",
                  cursor: "pointer",
                  fontSize: 20,
                }}
                onClick={() => props.onChatClick(item.name, item.category)}
              />
            )}
          </Box>
        ),
      };
    });
    setDocuments(tableValue);
  };

  const checkDocumentStatus = useCallback((docs: any[]) => {
    const hasAnyNotCompletedStatusDocument = docs.find(
      (d) => d.embedding_status !== "Completed"
    );
    if (hasAnyNotCompletedStatusDocument) {
      setRefresh(true);
      return true;
    } else {
      setRefresh(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const rawDocuments = await getDocumentsData(page, rowsPerPage);
      checkDocumentStatus(rawDocuments);
    };

    loadData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const startPolling = () => {
      intervalRef.current = setInterval(async () => {
        console.log("Polling for document status updates...");
        const rawDocuments = await getDocumentsData(page, rowsPerPage, true);
        const hasIncompleteDocuments = checkDocumentStatus(rawDocuments);
        if (!hasIncompleteDocuments) {
          console.log("All documents completed, stopping polling");
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 30000);
    };

    if (refresh) {
      console.log("Starting document status polling...");
      startPolling();
    } else {
      if (intervalRef.current) {
        console.log("Stopping document status polling...");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refresh, page, rowsPerPage, checkDocumentStatus]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleChangePage = async (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleFileTypeChange = (fileName: string, newType: string) => {
    setFileTypes((prev) => ({
      ...prev,
      [fileName]: newType,
    }));
  };

  const getSelectedFilesPreview = () => {
    if (!files.length) return null;

    const headers = [
      { name: t("name"), field: "name" },
      { name: t("type"), field: "type" },
      { name: t("action"), field: "action" },
    ];

    const rows = files.map((file, idx) => ({
      id: file.name + idx,
      name: <Typography sx={classes.tableLinkText}>{file.name}</Typography>,
      type: (
        <select
          value={fileTypes[file.name] || "Other"}
          onChange={(e) => handleFileTypeChange(file.name, e.target.value)}
          style={{
            background: "#282945",
            color: "#fff",
            border: "1px solid #9053F6",
            borderRadius: "4px",
            padding: "8px",
            width: "120px",
          }}
        >
          {fileTypeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ),
      action: (
        <CloseIcon
          sx={{
            color: "#9053F6",
            cursor: "pointer",
            fontSize: 20,
          }}
          onClick={() => handleRemoveFile(idx)}
        />
      ),
    }));

    return (
      <Box sx={{ ...classes.tableWrapper, mb: 2 }}>
        <CustomTable headers={headers} rows={rows} isRowPerPageEnable={false} />
        {files.length > 0 && (
          <CustomButton
            type="secondary"
            label={t("upload")}
            onClick={handleUploadFile}
            customClasses={{ width: "fit-content" }}
          />
        )}
      </Box>
    );
  };

  const handleUploadFile = async () => {
    try {
      if (files.length > 0) {
        props.setUploadingFiles(files);
        props.setShowUploadingTable(true);

        const formData = new FormData();
        files.forEach((file) => {
          formData.append("file", file);
        });

        const metadata = {
          files: files.map((file) => ({
            name: file.name,
            type: fileTypes[file.name] || "Other",
          })),
        };
        formData.append("metadata", JSON.stringify(metadata));

        const response = await ragUploadFile(formData);
        openSuccessNotification(response.message);
        setPage(1);
        props.setShowUploadingTable(false);
        props.setUploadingFiles([]);
        await getDocumentsData(1, rowsPerPage);
      } else {
        throw {
          errorMessage: "Please select file(s) to upload",
          errorCode: 500,
        };
      }
    } catch (error: any) {
      handleErrorMessage(error);
      props.setShowUploadingTable(false);
      props.setUploadingFiles([]);
    } finally {
      setFiles([]);
      setDropzoneKey((prev) => prev + 1);
      props.setIsLoading(false);
    }
  };

  const handleRemoveFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleChange = async (selectedFiles: File[]) => {
    setFiles(selectedFiles);
  };

  const getDropzoneArea = () => {
    return (
      <Stack direction="column" spacing={2} alignItems="center">
        <Box sx={{ position: "relative", width: "100%" }}>
          <DropzoneArea
            key={dropzoneKey}
            ref={dropzoneRef}
            dropzoneText=""
            onChange={handleChange}
            filesLimit={1000}
            acceptedFiles={["application/pdf", "image/*"]}
            showPreviews={false}
            showPreviewsInDropzone={false}
            useChipsForPreview
            previewText=""
            maxFileSize={Infinity}
            showAlerts={false}
            classes={{
              root: dropzoneClasses.dropzone,
              icon: dropzoneClasses.icon, // Hide default icon
              text: dropzoneClasses.text,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                justifyItems: "center",
                alignItems: "center",
                gap: 1,
                p: 2,
                textAlign: "center",
              }}
            >
              <CloudUploadIcon sx={{ color: "#ffffff", fontSize: 32 }} />
              <Typography
                sx={{ ...regularFont, color: "#ffffff90", fontSize: "22px" }}
              >
                {t("ragChatbotDropzoneText")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  };

  // Table for uploading files
  const getUploadingTable = () => {
    if (!props.showUploadingTable || props.uploadingFiles.length === 0)
      return null;
    const headers = [
      { name: t("name"), field: "name" },
      { name: t("status"), field: "status" },
    ];
    const rows = props.uploadingFiles.map((file, idx) => ({
      id: file.name + idx,
      name: <Typography sx={classes.tableLinkText}>{file.name}</Typography>,
      status: (
        <Typography sx={{ color: "#ffffff", ...regularFont }}>
          Uploading
        </Typography>
      ),
    }));
    return (
      <Box sx={{ ...classes.tableWrapper, mb: 2 }}>
        <CustomTable headers={headers} rows={rows} isRowPerPageEnable={false} />
      </Box>
    );
  };

  const getTable = () => {
    return (
      <Box sx={classes.tableWrapper}>
        <CustomTable
          headers={documentsHeader}
          rows={documents}
          isRowPerPageEnable={true}
          pageNumber={page}
          handlePageChange={handleChangePage}
          paginationCount={count}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
        />
      </Box>
    );
  };

  const getFileUploadArea = () => {
    return (
      <Grid container sx={{ placeContent: "center" }}>
        <Grid
          item
          xl={2.9}
          lg={3.9}
          md={3.9}
          sm={12}
          xs={12}
          pr={{ md: 1, xs: 0 }}
          pb={{ md: 0, xs: 1 }}
        >
          <Box p={2} pl={1}>
            {getDropzoneArea()}
          </Box>
        </Grid>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        />
        <Grid item xl={9} lg={8} md={8} sm={12} xs={12}>
          {/* Uploading files table above the main table */}
          {(!props.showUploadingTable || props.uploadingFiles.length === 0) &&
            getSelectedFilesPreview()}
          {getUploadingTable()}
          {getTable()}
        </Grid>
      </Grid>
    );
  };
  return getFileUploadArea();
};

export default FileUploadArea;
